import React, { PropsWithChildren, useEffect, useState, type JSX } from 'react';
import { useMarketplaceConfig } from '../queries/marketplace';
import type { Product } from '../types/enums';
import FullScreenLoader from '../components/atoms/FullScreenLoader';
import MaintenancePage from '../components/pages/MaintenancePage';

export const TRIGGER_MAINTENANCE_MODE_EVENT = 'triggerMaintenance';

export type MaintenanceProduct = Product;
type MaintenanceProductConfig = {
  runningFlag: 'feedRunning' | 'casinoRunning';
  supportsEventTrigger: boolean;
};

const MAINTENANCE_PRODUCT_CONFIG = {
  sportsbook: {
    runningFlag: 'feedRunning',
    supportsEventTrigger: true,
  },
  casino: {
    runningFlag: 'casinoRunning',
    supportsEventTrigger: false,
  },
} as const satisfies Record<MaintenanceProduct, MaintenanceProductConfig>;

type MaintenanceProtectedRouteProps = PropsWithChildren<{
  product?: MaintenanceProduct;
}>;

const MaintenanceProtectedRoute = ({
  children,
  product = 'sportsbook',
}: MaintenanceProtectedRouteProps): JSX.Element => {
  const [isEventTriggeredMaintenanceMode, setIsEventTriggeredMaintenanceMode] = useState(false);
  const { isLoading, data: marketPlaceData } = useMarketplaceConfig();
  const productConfig = MAINTENANCE_PRODUCT_CONFIG[product];
  const isConfigMaintenanceMode = marketPlaceData?.[productConfig.runningFlag] === false;
  const shouldShowMaintenancePage =
    isConfigMaintenanceMode || (productConfig.supportsEventTrigger && isEventTriggeredMaintenanceMode);

  useEffect(() => {
    const handleMaintenanceMode = () => {
      if (!productConfig.supportsEventTrigger) return;
      setIsEventTriggeredMaintenanceMode(true);
    };

    window.addEventListener(TRIGGER_MAINTENANCE_MODE_EVENT, handleMaintenanceMode);

    return () => {
      window.removeEventListener(TRIGGER_MAINTENANCE_MODE_EVENT, handleMaintenanceMode);
    };
  }, [productConfig]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return shouldShowMaintenancePage ? <MaintenancePage /> : <>{children}</>;
};

export default MaintenanceProtectedRoute;
